import { useState, useEffect } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Avatar,
  Badge,
  Box,
  Button,
  Select,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { X, Check, Mail } from 'react-feather';
import {
  usersApi,
  DEPARTMENTS,
  USER_ROLES,
  type InternalUserDetails,
  type UpdateInternalUserInput,
  type InternalPersona,
} from '../../services/api/users';
// import classes from './UserDetailsDrawer.module.css';

interface UserDetailsDrawerProps {
  opened: boolean;
  onClose: () => void;
  user: InternalUserDetails | null;
  onUpdate: () => void;
  onDeactivate: () => void;
  onActivate: () => void;
  onResendInvitation?: () => void;
  currentUserId?: string;
}

// Get initials from user
function getUserInitials(user: InternalUserDetails): string {
  const first = user.first_name?.[0] || '';
  const last = user.last_name?.[0] || '';
  return (first + last).toUpperCase() || user.email[0].toUpperCase();
}

// Get display name
function getUserDisplayName(user: InternalUserDetails): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.email;
}

// Format date for display
function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format last active for display
function formatLastActive(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(dateString);
}

type InvitationStatus = 'accepted' | 'pending' | 'expired';

export function UserDetailsDrawer({
  opened,
  onClose,
  user,
  onUpdate,
  onDeactivate,
  onActivate,
  onResendInvitation,
  currentUserId,
}: UserDetailsDrawerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editValues, setEditValues] = useState<UpdateInternalUserInput>({});

  // Simple effect: reset when drawer closes or user changes
  const userId = user?.id;
  useEffect(() => {
    if (!opened) {
      // Drawer closed - just reset editing state
      setIsEditing(false);
      return;
    }

    if (user) {
      // Drawer opened with a user - initialize edit values
      setEditValues({
        job_title: user.job_title || '',
        department: user.department || '',
        persona: user.persona,
      });
      setIsEditing(false);
    }
    // Depend only on primitives to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, userId]);

  if (!user) return null;

  const isOwnAccount = user.id === currentUserId;

  const handleSave = async () => {
    setLoading(true);
    try {
      await usersApi.update(user.id, editValues);
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update user',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValues({
      job_title: user.job_title || '',
      department: user.department || '',
      persona: user.persona,
    });
  };

  // Render persona badge
  const renderPersonaBadge = () => {
    const isAdmin = user.persona === 'internal_admin';
    return (
      <Badge
        variant={isAdmin ? 'filled' : 'outline'}
        color={isAdmin ? 'gray' : 'medleyGreen'}
        size="sm"
        style={{ textTransform: 'none', fontWeight: 500 }}
      >
        {user.persona_display}
      </Badge>
    );
  };

  // Render status badge
  const renderStatusBadge = () => {
    return (
      <Badge
        tt="uppercase"
        size="sm"
        variant={user.is_active ? 'filled' : 'outline'}
        color={user.is_active ? 'medleyGreen' : 'gray'}
        style={{
          padding: '4px 8px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.3px',
        }}
      >
        {user.is_active ? 'ACTIVE' : 'INACTIVE'}
      </Badge>
    );
  };

  // Compute invitation status - memoize to avoid recomputation
  const invitationStatus: InvitationStatus = (() => {
    if (user.email_verified_at) return 'accepted';
    if (user.invitation_expires_at) {
      const expiresAt = new Date(user.invitation_expires_at);
      if (expiresAt > new Date()) return 'pending';
      return 'expired';
    }
    return 'expired';
  })();

  // Render invitation status badge (only for users who haven't accepted)
  const renderInvitationStatusBadge = () => {
    if (invitationStatus === 'accepted') {
      return null;
    }

    const isPending = invitationStatus === 'pending';
    return (
      <Badge
        tt="uppercase"
        size="sm"
        variant="outline"
        color={isPending ? 'warningAmber' : 'gray'}
        style={{
          padding: '4px 8px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.3px',
        }}
      >
        {isPending ? 'INVITATION PENDING' : 'INVITATION EXPIRED'}
      </Badge>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="User Details"
      position="right"
      size="md"
      padding="lg"
    >
      <Stack gap="lg">
        {/* User Header */}
        <Stack align="center" gap="sm">
          <Avatar
            size={80}
            radius={80}
            color="medleyGreen"
            variant="filled"
            src={user.avatar_url}
          >
            {getUserInitials(user)}
          </Avatar>
          <Text fw={600} size="lg">{getUserDisplayName(user)}</Text>
          <Text size="sm" c="dimmed">{user.email}</Text>
          <Group gap="xs">
            {renderStatusBadge()}
            {renderInvitationStatusBadge()}
          </Group>
        </Stack>

        <Divider />

        {/* User Details */}
        {isEditing ? (
          /* Edit Mode */
          <Stack gap="md">
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Persona
              </Text>
              <Select
                data={[
                  { value: 'internal_admin', label: 'Admin' },
                  { value: 'internal_user', label: 'Cohort Manager' },
                ]}
                value={editValues.persona}
                onChange={(value) => setEditValues({ ...editValues, persona: value as InternalPersona })}
              />
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Role
              </Text>
              <Select
                data={USER_ROLES.map((role) => ({ value: role, label: role }))}
                value={editValues.job_title}
                onChange={(value) => setEditValues({ ...editValues, job_title: value || '' })}
                searchable
              />
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Department
              </Text>
              <Select
                data={DEPARTMENTS.map((dept) => ({ value: dept, label: dept }))}
                value={editValues.department}
                onChange={(value) => setEditValues({ ...editValues, department: value || '' })}
              />
            </Box>

            <Group justify="flex-end" mt="sm">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<X size={16} />}
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                leftSection={<Check size={16} />}
                onClick={handleSave}
                loading={loading}
              >
                Save
              </Button>
            </Group>
          </Stack>
        ) : (
          /* View Mode */
          <Stack gap="md">
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Persona
              </Text>
              {renderPersonaBadge()}
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Role
              </Text>
              <Text size="sm">{user.job_title || '—'}</Text>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Department
              </Text>
              <Text size="sm">{user.department || '—'}</Text>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Last Active
              </Text>
              <Text size="sm">{formatLastActive(user.last_login_at)}</Text>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Created
              </Text>
              <Text size="sm">{formatDate(user.created_at)}</Text>
            </Box>
          </Stack>
        )}

        <Divider />

        {/* Actions */}
        {!isEditing && (
          <Stack gap="sm">
            <Button
              variant="outline"
              fullWidth
              onClick={() => setIsEditing(true)}
            >
              Edit User
            </Button>
            {/* Resend Invitation - show when user hasn't accepted invitation */}
            {invitationStatus !== 'accepted' && onResendInvitation && (
              <Button
                variant="outline"
                color="blue"
                fullWidth
                leftSection={<Mail size={16} />}
                onClick={onResendInvitation}
              >
                Resend Invitation
              </Button>
            )}
            {!isOwnAccount && (
              user.is_active ? (
                <Button
                  variant="outline"
                  color="red"
                  fullWidth
                  onClick={onDeactivate}
                >
                  Deactivate User
                </Button>
              ) : (
                <Button
                  variant="outline"
                  color="green"
                  fullWidth
                  onClick={onActivate}
                >
                  Activate User
                </Button>
              )
            )}
          </Stack>
        )}
      </Stack>
    </Drawer>
  );
}

export default UserDetailsDrawer;
